import type { FC, ReactNode } from 'react';
import { Gauge, Package, User } from 'lucide-react';
import { CollapsibleFormSection } from './CollapsibleFormSection';
import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import { DurationInput } from '@/components/form/inputs/DurationInput';
import { ComboboxInput } from '@/components/form/inputs/ComboboxInput';
import { useTranslation } from '@/hooks/useTranslation';
import { Badge } from '@/components/ui/badge';
import { OPERATION_OPTIONS } from '../data/operationOptions';
import { ProcessFilteredFormSectionsB } from './ProcessFilteredFormSectionsB';

interface ProcessFilteredFormSectionsProps {
  visibleKeys: Set<string>;
  mandatoryFields: string[];
  formValues: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
}

function RequiredBadge() {
  const { t } = useTranslation();
  return (
    <Badge tone="primary" size="sm" className="ml-1">
      {t('common.required')}
    </Badge>
  );
}

function FieldWrapper({
  mandatory,
  children,
}: {
  mandatory: boolean;
  children: ReactNode;
}) {
  if (!mandatory) return <>{children}</>;
  return (
    <div className="relative">
      <div className="absolute right-0 top-0 z-10">
        <RequiredBadge />
      </div>
      {children}
    </div>
  );
}

export const ProcessFilteredFormSections: FC<ProcessFilteredFormSectionsProps> = ({
  visibleKeys,
  mandatoryFields,
  formValues,
  onChange,
}) => {
  const { t } = useTranslation();

  const isVisible = (key: string) => visibleKeys.has(key) || mandatoryFields.includes(key);
  const isMandatory = (key: string) => mandatoryFields.includes(key);

  const cuttingFields = ['ae', 'ap', 'vc', 'fz'];
  const machinedFields = ['volume', 'distance', 'pieces', 'duration_total', 'duration_piece', 'malrond'];
  const operatorFields = ['operator', 'operation'];

  const cuttingVisible = cuttingFields.filter(isVisible);
  const machinedVisible = machinedFields.filter(isVisible);
  const operatorVisible = operatorFields.filter(isVisible);

  const countLabel = (visible: number, total: number) =>
    t('traceability.processManager.form.sections.countLabel', { set: visible, total });

  return (
    <>
      {cuttingVisible.length > 0 && (
        <CollapsibleFormSection
          icon={<Gauge className="h-3 w-3" aria-hidden />}
          title={t('traceability.processSelection.sections.cutting')}
          toggleLabel={t('traceability.processSelection.sections.cutting')}
          countLabel={countLabel(cuttingVisible.length, cuttingFields.length)}
          defaultOpen
        >
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {isVisible('ae') && (
              <FieldWrapper mandatory={isMandatory('ae')}>
                <SimpleInput
                  name="ae"
                  label={t('traceability.processManager.form.prefillFields.ae.label')}
                  value={formValues.ae as number}
                  onChange={(v) => onChange('ae', v)}
                  type="number"
                  placeholder={t('traceability.processManager.form.prefillFields.ae.placeholder')}
                  required={isMandatory('ae')}
                />
              </FieldWrapper>
            )}
            {isVisible('ap') && (
              <FieldWrapper mandatory={isMandatory('ap')}>
                <SimpleInput
                  name="ap"
                  label={t('traceability.processManager.form.prefillFields.ap.label')}
                  value={formValues.ap as number}
                  onChange={(v) => onChange('ap', v)}
                  type="number"
                  placeholder={t('traceability.processManager.form.prefillFields.ap.placeholder')}
                  required={isMandatory('ap')}
                />
              </FieldWrapper>
            )}
            {isVisible('vc') && (
              <FieldWrapper mandatory={isMandatory('vc')}>
                <SimpleInput
                  name="vc"
                  label={t('traceability.processManager.form.prefillFields.vc.label')}
                  value={formValues.vc as number}
                  onChange={(v) => onChange('vc', v)}
                  type="number"
                  placeholder={t('traceability.processManager.form.prefillFields.vc.placeholder')}
                  required={isMandatory('vc')}
                />
              </FieldWrapper>
            )}
            {isVisible('fz') && (
              <FieldWrapper mandatory={isMandatory('fz')}>
                <SimpleInput
                  name="fz"
                  label={t('traceability.processManager.form.prefillFields.fz.label')}
                  value={formValues.fz as number}
                  onChange={(v) => onChange('fz', v)}
                  type="number"
                  placeholder={t('traceability.processManager.form.prefillFields.fz.placeholder')}
                  required={isMandatory('fz')}
                />
              </FieldWrapper>
            )}
          </div>
        </CollapsibleFormSection>
      )}

      {machinedVisible.length > 0 && (
        <CollapsibleFormSection
          icon={<Package className="h-3 w-3" aria-hidden />}
          title={t('traceability.processSelection.sections.machined')}
          toggleLabel={t('traceability.processSelection.sections.machined')}
          countLabel={countLabel(machinedVisible.length, machinedFields.length)}
          defaultOpen
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {isVisible('volume') && (
              <FieldWrapper mandatory={isMandatory('volume')}>
                <SimpleInput
                  name="volume"
                  label={t('traceability.processManager.form.prefillFields.machinedVolume.label')}
                  value={formValues.volume as number}
                  onChange={(v) => onChange('volume', v)}
                  type="number"
                  placeholder={t('traceability.processManager.form.prefillFields.machinedVolume.placeholder')}
                  required={isMandatory('volume')}
                />
              </FieldWrapper>
            )}
            {isVisible('distance') && (
              <FieldWrapper mandatory={isMandatory('distance')}>
                <SimpleInput
                  name="distance"
                  label={t('traceability.processManager.form.prefillFields.machinedDistance.label')}
                  value={formValues.distance as number}
                  onChange={(v) => onChange('distance', v)}
                  type="number"
                  placeholder={t('traceability.processManager.form.prefillFields.machinedDistance.placeholder')}
                  required={isMandatory('distance')}
                />
              </FieldWrapper>
            )}
            {isVisible('pieces') && (
              <FieldWrapper mandatory={isMandatory('pieces')}>
                <SimpleInput
                  name="pieces"
                  label={t('traceability.processManager.form.prefillFields.machinedPieces.label')}
                  value={formValues.pieces as number}
                  onChange={(v) => onChange('pieces', v)}
                  type="number"
                  placeholder={t('traceability.processManager.form.prefillFields.machinedPieces.placeholder')}
                  required={isMandatory('pieces')}
                />
              </FieldWrapper>
            )}
            {isVisible('malrond') && (
              <FieldWrapper mandatory={isMandatory('malrond')}>
                <SimpleInput
                  name="malrond"
                  label={t('traceability.processManager.form.prefillFields.malround.label')}
                  value={formValues.malrond as number}
                  onChange={(v) => onChange('malrond', v)}
                  type="number"
                  placeholder={t('traceability.processManager.form.prefillFields.malround.placeholder')}
                  required={isMandatory('malrond')}
                />
              </FieldWrapper>
            )}
            {isVisible('duration_total') && (
              <FieldWrapper mandatory={isMandatory('duration_total')}>
                <DurationInput
                  name="duration_total"
                  label={t('traceability.processManager.form.prefillFields.totalMachiningDuration.label')}
                  value={formValues.duration_total as string}
                  onChange={(v) => onChange('duration_total', v)}
                  required={isMandatory('duration_total')}
                />
              </FieldWrapper>
            )}
            {isVisible('duration_piece') && (
              <FieldWrapper mandatory={isMandatory('duration_piece')}>
                <DurationInput
                  name="duration_piece"
                  label={t('traceability.processManager.form.prefillFields.pieceMachiningDuration.label')}
                  value={formValues.duration_piece as string}
                  onChange={(v) => onChange('duration_piece', v)}
                  required={isMandatory('duration_piece')}
                />
              </FieldWrapper>
            )}
          </div>
        </CollapsibleFormSection>
      )}

      {operatorVisible.length > 0 && (
        <CollapsibleFormSection
          icon={<User className="h-3 w-3" aria-hidden />}
          title={t('traceability.processSelection.sections.operatorInfo')}
          toggleLabel={t('traceability.processSelection.sections.operatorInfo')}
          countLabel={countLabel(operatorVisible.length, operatorFields.length)}
          defaultOpen
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {isVisible('operator') && (
              <FieldWrapper mandatory={isMandatory('operator')}>
                <SimpleInput
                  name="operator"
                  label={t('traceability.processManager.form.prefillFields.operator.label')}
                  value={formValues.operator as string}
                  onChange={(v) => onChange('operator', v)}
                  placeholder={t('traceability.processManager.form.prefillFields.operator.placeholder')}
                  required={isMandatory('operator')}
                />
              </FieldWrapper>
            )}
            {isVisible('operation') && (
              <FieldWrapper mandatory={isMandatory('operation')}>
                <ComboboxInput
                  name="operation"
                  label={t('traceability.processManager.form.prefillFields.operation.label')}
                  value={(formValues.operation as string) ?? null}
                  onChange={(v) => onChange('operation', v)}
                  options={OPERATION_OPTIONS}
                  placeholder={t('traceability.generalTab.operationPlaceholder')}
                />
              </FieldWrapper>
            )}
          </div>
        </CollapsibleFormSection>
      )}

      <ProcessFilteredFormSectionsB
        visibleKeys={visibleKeys}
        mandatoryFields={mandatoryFields}
        formValues={formValues}
        onChange={onChange}
      />
    </>
  );
};
